import { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { useNotification } from "@/context/NotificationContext";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import {
  clearEnrollmentsWaitlistState,
  fetchEventEnrollmentsAction,
  fetchEventWaitlistItemsAction,
  fetchWaitlistDetailsAction,
  updateWaitlistPositionAction,
  moveWaitlistToEnrollmentAction,
  removeWaitlistItemAction,
  // NEW ACTION: move enrollment -> waitlist
  moveEnrollmentToWaitlistAction,
  fetchEventSessionAttendancesAction,
  updateEventSessionAttendanceAction,
  bulkUpdateEventSessionAttendanceAction,
  exportEventAttendanceCsvAction,
  setSelectedAttendanceStatusId as setSelectedAttendanceStatusIdAction,
  finalizeEventSessionAction,
} from "@/modules/eventRegistration/features/EventWaitlist/slices/enrollmentsWaitlistSlice";
import { EventEnrollmentItemResponseDTO } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventEnrollments/types";
import { EventWaitlistItemResponse } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventWaitlistItems/types";
import { UpdateEventSessionAttendanceRequest } from "@/modules/eventRegistration/features/EventWaitlist/api/updateEventSessionAttendance/types";
import { downloadFile } from "@/utils/downloadFile";

export type RegisteredUser = EventEnrollmentItemResponseDTO;
export type WaitlistEntry = EventWaitlistItemResponse;

interface UseWaitlistProps {
  eventSessionUuid?: string;
}

export default function useWaitlist({
  eventSessionUuid,
}: UseWaitlistProps = {}) {
  const { toast } = useToast();
  const { showNotification } = useNotification();
  const dispatch = useDispatch<AppDispatch>();

  const {
    enrollments,
    waitlistItems,
    waitlistDetails,
    errorUpdatePosition,
    attendances,
    loadingAttendances,
    loadingUpdateAttendance,
    errorUpdateAttendance,
    loadingExportCsv,
    selectedAttendanceStatusId,
  } = useSelector((state: RootState) => state.enrollmentsWaitlist);

  const [activeTab, setActiveTab] = useState<
    "waitlist" | "registered" | "attendance" | "transactions"
  >("waitlist");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isMovingToWaitlist, setIsMovingToWaitlist] = useState(false);
  const [isReplacing] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isChangingPosition, setIsChangingPosition] = useState(false);
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState<
    string | null
  >(null);

  const setSelectedAttendanceStatusId = (value: number | null) => {
    dispatch(setSelectedAttendanceStatusIdAction(value));
  };

  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showMoveToWaitlistDialog, setShowMoveToWaitlistDialog] =
    useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showFinalizeSessionDialog, setShowFinalizeSessionDialog] =
    useState(false);

  const [selectedUser, setSelectedUser] = useState<WaitlistEntry | null>(null);
  const [selectedRegistration, setSelectedRegistration] =
    useState<RegisteredUser | null>(null);

  useEffect(() => {
    if (!eventSessionUuid) return;

    (async () => {
      try {
        await Promise.all([
          dispatch(fetchEventEnrollmentsAction({ uuid: eventSessionUuid })),
          dispatch(fetchEventWaitlistItemsAction({ uuid: eventSessionUuid })),
          dispatch(fetchWaitlistDetailsAction({ uuid: eventSessionUuid })),
          dispatch(
            fetchEventSessionAttendancesAction({
              uuid: eventSessionUuid,
              statusId: undefined,
            }),
          ),
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast({
          title: "Error",
          description: "Failed to fetch event data. Please try again.",
          variant: "destructive",
        });
      }
    })();

    return () => {
      dispatch(clearEnrollmentsWaitlistState());
    };
  }, [eventSessionUuid, dispatch, toast]);

  useEffect(() => {
    if (!eventSessionUuid) return;

    (async () => {
      try {
        await dispatch(
          fetchEventSessionAttendancesAction({
            uuid: eventSessionUuid,
            statusId: selectedAttendanceStatusId || undefined,
          }),
        );
      } catch (error) {
        console.error("Error fetching attendance data:", error);
        toast({
          title: "Error",
          description: "Failed to fetch attendance data. Please try again.",
          variant: "destructive",
        });
      }
    })();
  }, [selectedAttendanceStatusId, eventSessionUuid, dispatch, toast]);

  const handleTabChange = (value: string) => {
    if (
      value === "waitlist" ||
      value === "registered" ||
      value === "attendance" ||
      value === "transactions"
    ) {
      setActiveTab(
        value as "waitlist" | "registered" | "attendance" | "transactions",
      );
      if (value === "attendance") {
        setSelectedAttendanceDate(null);
      }
    }
  };

  const refreshData = async () => {
    if (!eventSessionUuid) return;
    setIsRefreshing(true);
    try {
      await dispatch(fetchEventEnrollmentsAction({ uuid: eventSessionUuid }));
      await dispatch(fetchEventWaitlistItemsAction({ uuid: eventSessionUuid }));
      await dispatch(fetchWaitlistDetailsAction({ uuid: eventSessionUuid }));
      await dispatch(
        fetchEventSessionAttendancesAction({
          uuid: eventSessionUuid,
          statusId: selectedAttendanceStatusId || undefined,
        }),
      );
      toast({
        title: "Data refreshed",
        description: "The waitlist data has been refreshed.",
      });
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast({
        title: "Error",
        description: "Failed to refresh data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Remove a user from the waitlist
   */
  const removeFromWaitlist = async (registrationId: number) => {
    if (!eventSessionUuid) return;
    setIsProcessing(true);
    try {
      await dispatch(
        removeWaitlistItemAction({
          uuid: eventSessionUuid,
          eventWaitlistId: registrationId,
        }),
      );
      toast({
        title: "Removed from waitlist",
        description: "User has been removed from the waitlist.",
      });
      // Refresh the data after the removal
      await refreshData();
    } catch (error) {
      console.error("Error removing from waitlist:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove waitlist item.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Move a registration to the waitlist (not used in your snippet but here as an example)
   */
  const moveToWaitlist = async (registration: RegisteredUser) => {
    console.log("moveToWaitlist called with registration:", registration);
  };

  /**
   * Register a user from the waitlist (move waitlist to enrollment)
   */
  const registerFromWaitlist = async (waitlistId: number) => {
    if (!eventSessionUuid) return;
    setIsRegistering(true);
    try {
      await dispatch(
        moveWaitlistToEnrollmentAction({
          uuid: eventSessionUuid,
          eventWaitlistId: waitlistId,
        }),
      );
      toast({
        title: "User registered",
        description:
          "User has been successfully moved from waitlist to enrollments.",
      });
      // Refresh the data after the move
      await refreshData();
    } catch (error) {
      console.error("Error moving waitlist to enrollment:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to move waitlist to enrollment.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsRegistering(false);
    }
  };

  /**
   * NEW: Move an existing enrollment -> waitlist
   */
  const moveFromEnrollmentToWaitlist = async (enrollmentId: number) => {
    if (!eventSessionUuid) return;
    setIsMovingToWaitlist(true);
    try {
      await dispatch(
        moveEnrollmentToWaitlistAction({
          uuid: eventSessionUuid,
          enrollmentId: enrollmentId,
        }),
      );
      toast({
        title: "Moved to waitlist",
        description: "The user has been moved from enrollment to the waitlist.",
      });
      // Refresh data
      await refreshData();
    } catch (error) {
      console.error("Error moving enrollment to waitlist:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to move enrollment to waitlist.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsMovingToWaitlist(false);
    }
  };

  /**
   * Replace a registration with a new employee
   */
  const replaceEmployee = async (
    registrationId: string,
    newEmployeeId: string,
  ) => {
    console.log(
      "replaceEmployee called - OldReg:",
      registrationId,
      " NewEmp:",
      newEmployeeId,
    );
  };

  /**
   * Update a waitlist item's position optimistically, then refresh or revert on error.
   */
  const updateWaitlistPosition = async (
    waitlistId: number,
    newPosition: number,
  ) => {
    if (!eventSessionUuid) return;

    setIsChangingPosition(true);
    try {
      await dispatch(
        updateWaitlistPositionAction(
          {
            uuid: eventSessionUuid,
            eventWaitlistId: waitlistId,
            newPosition: newPosition,
          },
          () =>
            dispatch(fetchEventWaitlistItemsAction({ uuid: eventSessionUuid })),
        ),
      );

      if (errorUpdatePosition) {
        throw new Error(errorUpdatePosition);
      }
    } catch (error) {
      console.error("Error updating position:", error);
      throw error;
    } finally {
      setIsChangingPosition(false);
    }
  };

  /**
   * Move a waitlist item to the top
   */
  const moveWaitlistItemToTop = async (waitlistId: number) => {
    if (!eventSessionUuid) return;

    setIsChangingPosition(true);
    try {
      await dispatch(
        updateWaitlistPositionAction(
          {
            uuid: eventSessionUuid,
            eventWaitlistId: waitlistId,
            newPosition: 1,
          },
          () =>
            dispatch(fetchEventWaitlistItemsAction({ uuid: eventSessionUuid })),
        ),
      );

      if (errorUpdatePosition) {
        throw new Error(errorUpdatePosition);
      }
    } catch (error) {
      console.error("Error moving to top:", error);
      throw error;
    } finally {
      setIsChangingPosition(false);
    }
  };

  /**
   * Update event session attendance (status and/or notes)
   */
  const updateAttendance = async (
    attendanceId: number,
    data: UpdateEventSessionAttendanceRequest,
  ) => {
    try {
      await dispatch(
        updateEventSessionAttendanceAction(attendanceId, data, async () => {
          const successMessage = "Attendance record updated successfully";

          toast({
            title: "Success",
            description: successMessage,
          });

          const sessionUuid = eventSessionUuid || waitlistDetails?.uuid;
          if (sessionUuid) {
            return dispatch(
              fetchEventSessionAttendancesAction({
                uuid: sessionUuid,
                statusId: selectedAttendanceStatusId || undefined,
              }),
            );
          }
        }),
      );
    } catch {
      const errorMessage = "Failed to update attendance record";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  /**
   * Bulk update event session attendance status
   */
  const bulkUpdateAttendanceStatus = async (
    attendanceIds: number[],
    statusId: number,
  ) => {
    try {
      await dispatch(
        bulkUpdateEventSessionAttendanceAction(
          { attendanceIds, statusId },
          async () => {
            const successMessage = `${attendanceIds.length} attendance records updated successfully`;

            toast({
              title: "Success",
              description: successMessage,
            });

            const sessionUuid = eventSessionUuid || waitlistDetails?.uuid;
            if (sessionUuid) {
              return dispatch(
                fetchEventSessionAttendancesAction({
                  uuid: sessionUuid,
                  statusId: selectedAttendanceStatusId || undefined,
                }),
              );
            }
          },
        ),
      );
    } catch {
      const errorMessage = "Failed to update attendance records";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  /**
   * Finalize the event session
   */
  const finalizeSession = async () => {
    if (!eventSessionUuid) {
      toast({
        title: "Error",
        description: "No event session found to finalize.",
        variant: "destructive",
      });
      return;
    }

    try {
      await dispatch(
        finalizeEventSessionAction({ uuid: eventSessionUuid }, () => {
          showNotification(
            "Success!",
            "The event session has been successfully finalized.",
            "success",
          );
          setShowFinalizeSessionDialog(false);
          refreshData();
        }),
      );
    } catch (error) {
      console.error("Error finalizing session:", error);
      const message =
        error instanceof Error ? error.message : "Failed to finalize session.";
      showNotification("Error!", message, "error");
    }
  };

  /**
   * Export attendance data as CSV
   */
  const handleExportAttendanceCsv = async () => {
    const sessionUuid = eventSessionUuid || waitlistDetails?.uuid;
    if (!sessionUuid) {
      toast({
        title: "Error",
        description: "No event session found to export attendance data.",
        variant: "destructive",
      });
      return;
    }

    try {
      await dispatch(
        exportEventAttendanceCsvAction({ uuid: sessionUuid }, (blob: Blob) => {
          const fileName = `event_session_${sessionUuid}_attendance_${new Date().toISOString().split("T")[0]}.csv`;
          downloadFile(blob, fileName);
          toast({
            title: "Export completed",
            description: "Attendance data has been exported successfully.",
          });
        }),
      );
    } catch (error) {
      console.error("Error exporting attendance CSV:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to export attendance data.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  /**
   * Utility to format dates
   */
  const formatDate = (dateString: string): string => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy h:mm a");
    } catch {
      return "Invalid date";
    }
  };

  /**
   * Flatten store data so the UI can easily consume
   */
  const registeredUsers: RegisteredUser[] = enrollments;
  const waitlist: WaitlistEntry[] = waitlistItems;

  const attendanceDates = Object.keys(attendances).sort();

  const currentAttendances = selectedAttendanceDate
    ? attendances[selectedAttendanceDate] || []
    : attendanceDates.length > 0
      ? attendances[attendanceDates[0]] || []
      : [];

  return {
    activeTab,
    isProcessing,
    isRefreshing,
    isMovingToWaitlist,
    isReplacing,
    isRegistering,
    isChangingPosition,
    waitlist,
    registeredUsers,
    waitlistDetails,
    showRemoveDialog,
    showMoveToWaitlistDialog,
    showReplaceDialog,
    showRegisterDialog,
    showFinalizeSessionDialog,
    selectedUser,
    selectedRegistration,
    attendances,
    loadingAttendances,
    attendanceDates,
    currentAttendances,
    selectedAttendanceDate:
      selectedAttendanceDate ||
      (attendanceDates.length > 0 ? attendanceDates[0] : null),
    selectedAttendanceStatusId,
    setSelectedAttendanceDate,
    setSelectedAttendanceStatusId,
    handleTabChange,
    refreshData,
    removeFromWaitlist,
    moveToWaitlist,
    registerFromWaitlist,
    moveFromEnrollmentToWaitlist,
    replaceEmployee,
    updateWaitlistPosition,
    moveWaitlistItemToTop,
    formatDate,
    setShowRemoveDialog,
    setShowMoveToWaitlistDialog,
    setShowReplaceDialog,
    setShowRegisterDialog,
    setShowFinalizeSessionDialog,
    setSelectedRegistration,
    setSelectedUser,
    handleRegisterClick: (user: WaitlistEntry) => {
      setSelectedUser(user);
      setShowRegisterDialog(true);
    },
    handleRemoveClick: (user: WaitlistEntry) => {
      setSelectedUser(user);
      setShowRemoveDialog(true);
    },
    handleReplaceClick: (registration: RegisteredUser) => {
      setSelectedRegistration(registration);
      setShowReplaceDialog(true);
    },
    handleMoveToWaitlistClick: (registration: RegisteredUser) => {
      setSelectedRegistration(registration);
      setShowMoveToWaitlistDialog(true);
    },
    updateAttendance,
    bulkUpdateAttendanceStatus,
    handleExportAttendanceCsv,
    finalizeSession,
    isUpdatingAttendance: loadingUpdateAttendance,
    updateAttendanceError: errorUpdateAttendance,
    isExportingCsv: loadingExportCsv,
  };
}
